import { z } from 'zod';

// ==========================================
// MIME Type Constants
// ==========================================

export const RESUME_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export const PDF_ONLY_MIME_TYPES = [
  'application/pdf',
] as const;

// ==========================================
// Magic Bytes (File Signatures)
// ==========================================
// The browser resolves file.type from the FILE EXTENSION, not the actual
// file content. A renamed malware.exe → malware.pdf will report
// file.type = 'application/pdf' and bypass MIME-only checks.
// Reading the raw binary header defeats this attack entirely.

/** Known file signature definitions */
const FILE_SIGNATURES = {
  /** PDF: %PDF */
  pdf: { bytes: [0x25, 0x50, 0x44, 0x46], offset: 0 },
  /** Legacy MS Office .doc (Compound Document) */
  doc: { bytes: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], offset: 0 },
  /** DOCX / XLSX / PPTX are ZIP containers — PK\x03\x04 */
  docx: { bytes: [0x50, 0x4B, 0x03, 0x04], offset: 0 },
} as const;

type KnownSignature = keyof typeof FILE_SIGNATURES;

/**
 * Reads the first 8 bytes of a File and returns which known signature
 * it matches, or null if it doesn't match any expected type.
 *
 * @param file  - The File object to inspect.
 * @param allow - Which signatures to accept.
 * @returns     Promise resolving to the matched key or null.
 */
export const detectFileMagicBytes = (
  file: File,
  allow: KnownSignature[]
): Promise<KnownSignature | null> => {
  return new Promise((resolve) => {
    const HEADER_BYTES = 8;
    const blob = file.slice(0, HEADER_BYTES);
    const reader = new FileReader();

    reader.onloadend = () => {
      const buffer = reader.result as ArrayBuffer;
      if (!buffer || buffer.byteLength === 0) {
        resolve(null);
        return;
      }
      const bytes = new Uint8Array(buffer);

      for (const key of allow) {
        const sig = FILE_SIGNATURES[key];
        const match = sig.bytes.every(
          (b: number, i: number) => bytes[sig.offset + i] === b
        );
        if (match) {
          resolve(key);
          return;
        }
      }
      resolve(null);
    };

    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Async gate for Ant Design's beforeUpload prop.
 * Checks BOTH the declared MIME type AND the actual magic bytes so that
 * a renamed .exe cannot slip through as a .pdf.
 *
 * Allowed signatures: PDF, DOC (legacy), DOCX (ZIP-based)
 *
 * @returns  Promise<true>  — accept the file
 * @returns  Promise<string> — reject with an error message
 */
export const validateJobFileBytes = async (file: File): Promise<true | string> => {
  const allowedMime = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  // 1. Quick extension/MIME guard (fast, catches obvious wrong types)
  if (!allowedMime.includes(file.type)) {
    return 'Only PDF, DOC, or DOCX files are allowed.';
  }

  // 2. Size guard
  const MAX = 10 * 1024 * 1024; // 10 MB
  if (file.size === 0) return 'The file is empty. Please upload a valid file.';
  if (file.size > MAX) return 'File size must be less than 10 MB.';

  // 3. Magic bytes — the critical check that catches renamed executables
  const detected = await detectFileMagicBytes(file, ['pdf', 'doc', 'docx']);
  if (detected === null) {
    return (
      'This file does not appear to be a real PDF, DOC, or DOCX. ' +
      'Renaming another file type (e.g. .exe → .pdf) is not allowed.'
    );
  }

  return true;
};

/**
 * Same as validateJobFileBytes but restricted to PDF only.
 * Use this wherever you want strictly PDF uploads.
 */
export const validatePdfFileBytes = async (file: File): Promise<true | string> => {
  if (file.type !== 'application/pdf') {
    return 'Only PDF files are allowed.';
  }

  if (file.size === 0) return 'The file is empty. Please upload a valid file.';
  const MAX = 10 * 1024 * 1024;
  if (file.size > MAX) return 'File size must be less than 10 MB.';

  const detected = await detectFileMagicBytes(file, ['pdf']);
  if (detected === null) {
    return (
      'This file does not appear to be a real PDF. ' +
      'Renaming another file type (e.g. .exe → .pdf) is not allowed.'
    );
  }

  return true;
};

export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export const CSV_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

// ==========================================
// Size Constants
// ==========================================

export const MAX_RESUME_SIZE = 10 * 1024 * 1024;   // 10MB
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024;     // 2MB
export const MAX_CSV_SIZE = 10 * 1024 * 1024;      // 10MB
export const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;  // 10MB

// ==========================================
// Helper: Format file size for error messages
// ==========================================

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ==========================================
// Zod Schemas — Single File
// ==========================================

/** Resume file: PDF, DOC, DOCX — max 5MB */
export const resumeFileSchema = z
  .instanceof(File, { message: 'Please upload a file.' })
  .refine((file) => file.size > 0, {
    message: 'File is empty. Please upload a valid file.',
  })
  .refine((file) => file.size <= MAX_RESUME_SIZE, {
    message: `File size must be less than ${formatFileSize(MAX_RESUME_SIZE)}.`,
  })
  .refine((file) => RESUME_MIME_TYPES.includes(file.type as any), {
    message: 'Only PDF, DOC, or DOCX files are allowed.',
  });

/** PDF-only file — max 5MB */
export const pdfFileSchema = z
  .instanceof(File, { message: 'Please upload a file.' })
  .refine((file) => file.size > 0, {
    message: 'File is empty. Please upload a valid file.',
  })
  .refine((file) => file.size <= MAX_RESUME_SIZE, {
    message: `File size must be less than ${formatFileSize(MAX_RESUME_SIZE)}.`,
  })
  .refine((file) => PDF_ONLY_MIME_TYPES.includes(file.type as any), {
    message: 'Only PDF files are allowed.',
  });

/** Image file: JPG, PNG, WebP, GIF — max 2MB */
export const imageFileSchema = z
  .instanceof(File, { message: 'Please upload an image.' })
  .refine((file) => file.size > 0, {
    message: 'File is empty. Please upload a valid image.',
  })
  .refine((file) => file.size <= MAX_IMAGE_SIZE, {
    message: `Image size must be less than ${formatFileSize(MAX_IMAGE_SIZE)}.`,
  })
  .refine((file) => IMAGE_MIME_TYPES.includes(file.type as any), {
    message: 'Only JPG, PNG, WebP, or GIF images are allowed.',
  });

/** CSV/Excel file — max 10MB */
export const csvFileSchema = z
  .instanceof(File, { message: 'Please upload a file.' })
  .refine((file) => file.size > 0, {
    message: 'File is empty. Please upload a valid file.',
  })
  .refine((file) => file.size <= MAX_CSV_SIZE, {
    message: `File size must be less than ${formatFileSize(MAX_CSV_SIZE)}.`,
  })
  .refine((file) => CSV_MIME_TYPES.includes(file.type as any), {
    message: 'Only CSV or Excel files are allowed.',
  });

/** General document: PDF, JPG, PNG, DOCX — max 10MB */
export const documentFileSchema = z
  .instanceof(File, { message: 'Please upload a document.' })
  .refine((file) => file.size > 0, {
    message: 'File is empty. Please upload a valid document.',
  })
  .refine((file) => file.size <= MAX_DOCUMENT_SIZE, {
    message: `File size must be less than ${formatFileSize(MAX_DOCUMENT_SIZE)}.`,
  })
  .refine((file) => DOCUMENT_MIME_TYPES.includes(file.type as any), {
    message: 'Only PDF, JPG, PNG, or DOCX files are allowed.',
  });

// ==========================================
// Zod Schemas — Multiple Files
// ==========================================

/** Multiple resumes: 1–20 files, each PDF/DOC/DOCX, max 5MB */
export const multiResumeSchema = z
  .array(resumeFileSchema)
  .min(1, 'Please upload at least one resume.')
  .max(20, 'Maximum 20 files allowed.');

/** Multiple PDF-only files: 1–20 files, max 5MB each */
export const multiPdfSchema = z
  .array(pdfFileSchema)
  .min(1, 'Please upload at least one file.')
  .max(20, 'Maximum 20 files allowed.');

// ==========================================
// Utility: Validate a single file (returns error string or null)
// ==========================================

export const validateFile = (
  file: File,
  schema: z.ZodType = resumeFileSchema
): string | null => {
  const result = schema.safeParse(file);
  if (!result.success) {
    return result.error.issues[0]?.message || 'Invalid file.';
  }
  return null;
};

// ==========================================
// Utility: Validate multiple files (returns error string or null)
// ==========================================

export const validateFiles = (
  files: File[],
  schema: z.ZodType = multiResumeSchema
): string | null => {
  const result = schema.safeParse(files);
  if (!result.success) {
    return result.error.issues[0]?.message || 'Invalid files.';
  }
  return null;
};
